import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDisplayDate } from "@/lib/dateFormat";
import { listPlatformIngresoSimplificado } from "@/services/ingresoSimplificadoService";

/**
 * Platform-admin queue of homologación-simplificada requests the Fiscalo team
 * works (SALKANTAI = Proveedor Habilitado). Includes self-scratch tenants who
 * handed homologación to us. Root / root-admin only (guarded in App routes).
 */
export function PlatformHomologacionesPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["platform", "ingreso-simplificado"] as const,
    queryFn: () => listPlatformIngresoSimplificado(),
    refetchInterval: 30_000,
  });
  const rows = data?.data ?? [];

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("platformHomologaciones.title", "Homologaciones")}
        description={t(
          "platformHomologaciones.description",
          "Altas de emisor electrónico (homologación simplificada) que el equipo está gestionando.",
        )}
      />
      <TableCard>
        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={t(
              "platformHomologaciones.emptyTitle",
              "No hay homologaciones activas",
            )}
            message={t(
              "platformHomologaciones.emptyMessage",
              "Cuando un emisor nuevo inicie su homologación va a aparecer acá.",
            )}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t("platformHomologaciones.company", "Empresa")}
                </TableHead>
                <TableHead>
                  {t("platformHomologaciones.status", "Estado")}
                </TableHead>
                <TableHead>
                  {t("platformHomologaciones.created", "Iniciada")}
                </TableHead>
                <TableHead>
                  {t("platformHomologaciones.vigencia", "Vigencia")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.companyId}
                  className="cursor-pointer transition-colors duration-150 hover:bg-muted/50"
                  onClick={() => navigate(`/homologaciones/${r.companyId}`)}
                >
                  <TableCell>
                    <div className="font-medium">{r.tenantName}</div>
                    <div className="text-xs text-muted-foreground">
                      RUT {r.tenantRut}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs uppercase">
                      {r.status}
                    </span>
                    {r.failureReason ? (
                      <div className="text-xs text-destructive">
                        {r.failureReason}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>{formatDisplayDate(r.createdAt)}</TableCell>
                  <TableCell>{r.fechaVigencia ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableCard>
    </div>
  );
}
